// Scelta della competizione di una partita: le competizioni attive del team
// piu' "Nuova competizione", che apre un campo di testo. Il valore e' il nome:
// il DB (trigger matches_sync_competition) trova o crea la competizione e
// scrive competition_id, cosi' lista in Impostazioni e partite restano
// allineate anche per chi salva solo il testo.
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@v2/lib/utils";
import { Input } from "@v2/components/ui/input";
import { useTeamCompetitions } from "../lib/team-config";

const NONE = "__none__";
const NEW = "__new__";

export function CompetitionPicker({
  teamId,
  value,
  onChange,
  className,
  name,
}: {
  teamId: string;
  value: string;
  onChange: (name: string) => void;
  className?: string;
  /** Se presente, il nome scelto finisce anche in un input nascosto con questo name (per i form a FormData). */
  name?: string;
}) {
  const { t } = useTranslation("sessions");
  const competitionsQuery = useTeamCompetitions(teamId);
  const competitions = competitionsQuery.data || [];
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState("");

  const trimmed = value.trim();
  const known = competitions.find((c) => c.name.trim().toLowerCase() === trimmed.toLowerCase());
  // Un nome non in lista (partita vecchia o appena digitato) resta visibile come voce propria.
  const selectValue = creating ? NEW : trimmed ? (known ? known.name : trimmed) : NONE;

  useEffect(() => {
    if (!creating) setDraft("");
  }, [creating]);

  const options = competitions
    .filter((c) => c.is_active || c.name === known?.name)
    .map((c) => c.name);
  if (trimmed && !known) options.push(trimmed);

  return (
    <div className={cn("flex items-center gap-2 min-w-0", className)}>
      {name && <input type="hidden" name={name} value={trimmed} />}
      <select
        value={selectValue}
        onChange={(e) => {
          const v = e.target.value;
          if (v === NEW) {
            setCreating(true);
            return;
          }
          setCreating(false);
          onChange(v === NONE ? "" : v);
        }}
        className={cn(
          "h-11 rounded-[14px] bg-raised px-3 text-sm min-w-0",
          creating ? "w-[110px]" : "flex-1",
        )}
        aria-label={t("competitionPicker.label")}
      >
        <option value={NONE}>{t("competitionPicker.none")}</option>
        {options.map((n) => (
          <option key={n} value={n}>{n}</option>
        ))}
        <option value={NEW}>{t("competitionPicker.new")}</option>
      </select>
      {creating && (
        <Input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => {
            const v = draft.trim();
            if (v) onChange(v);
            setCreating(false);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              (e.target as HTMLInputElement).blur();
            }
            if (e.key === "Escape") {
              setDraft("");
              setCreating(false);
            }
          }}
          placeholder={t("competitionPicker.newPh")}
          className="flex-1 min-w-[120px]"
        />
      )}
    </div>
  );
}
